import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { ConfirmDialogService, UiKitModule } from '@serviceplan/ui-kit';
import { AssetBasketService, DownloadAssetsService } from '@thehubz/client/asset-services';
import {
  AppSettings,
  AssetUIModel,
  AssetsGenerateUrlService,
  ClientCoreModule,
  ClientNotificationHandlerService,
  CommonUtilityLib,
  SearchAssetsFieldPlaceholder,
  accessAsset,
} from '@thehubz/client/core';
import { AssetsApiService } from '@thehubz/client/data-access-api';
import { AnyType } from '@thehubz/shared/model';
import { tap } from 'rxjs';
import { AssetCommonService } from '../../services/asset-common.service';
import { CollectionStepperDialogService } from '../../services/collection-stepper-dialog.service';
import { AssetBulkEditComponent } from '../asset-bulk-edit/asset-bulk-edit.component';
import { AssetDownloadComponent, AssetDownloadData } from '../asset-download/asset-download.component';
import { AssetFilterComponent } from '../asset-filter/asset-filter.component';

@Component({
  selector: 'thehubz-asset-basket',
  templateUrl: './asset-basket.component.html',
  styleUrls: ['./asset-basket.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatIconModule,
    MatMenuModule,
    MatCheckboxModule,
    MatTooltipModule,
    ClientCoreModule,
    UiKitModule,
    AssetFilterComponent,
    AssetBulkEditComponent,
  ],
})
export class AssetBasketComponent implements OnInit {
  assetsDetailList: AssetUIModel[] = [];
  filteredList: AssetUIModel[] = [];
  searchText = '';
  searchPlaceholder = SearchAssetsFieldPlaceholder;
  itemSize = 10;
  basketCount = 0;
  filterObject: AnyType = {
    filters: {},
    sort: { createdAt: -1 },
  };
  appliedFilters = 0;
  basketDataLoaded = false;
  filterDetails: AnyType = {};

  /**
   * True when data is loading from server
   */
  httpCallInProgress = false;
  isMobileSearchOpen = false;

  AccessAsset = accessAsset;

  constructor(
    private router: Router,
    private assetApiService: AssetsApiService,
    private assetUrlService: AssetsGenerateUrlService,
    private clientNotificationService: ClientNotificationHandlerService,
    private assetBasketService: AssetBasketService,
    private confirmDialog: ConfirmDialogService,
    private commonUtilityLib: CommonUtilityLib,
    protected assetCommonService: AssetCommonService,
    protected collectionStepperDialogService: CollectionStepperDialogService,
    private downloadAssetsService: DownloadAssetsService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.commonUtilityLib.emitNewHeaderline($localize`Basket: ${this.basketCount}`);
    const assetIds: string[] = this.assetBasketService.assetIdsFromMyBasket;
    if (!assetIds.length) {
      this.basketDataLoaded = true;
      return;
    }

    this._showLoader();
    if (assetIds.length > 200) {
      const splitedIds = this.splitAssetIds(assetIds);
      let count = 0;
      splitedIds.forEach((ids) => {
        this.initializeAssets(ids, assetIds.length);
        count += ids.length;
        this.commonUtilityLib.emitNewHeaderline($localize`:headline:Basket: ${count}`);
      });
    } else {
      this.initializeAssets(assetIds, assetIds.length);
    }
  }

  initializeAssets(assetIds: AnyType, assetCount: number) {
    this.assetApiService
      .assetsControllerFindAll('', JSON.stringify(this.filterObject), assetIds)
      .pipe(tap(() => this._showLoader(false)))
      .subscribe((response) => {
        const responseData = this.assetUrlService.generatePreview(response.result ?? []);
        this.basketCount = responseData.length;
        responseData.forEach((asset: AssetUIModel) => {
          asset['assetExpiryDays'] = this.assetCommonService.getAssetExpireDays(asset.expiryDate);
        });
        if (assetCount < 200 && this.basketCount) {
          this.commonUtilityLib.emitNewHeaderline($localize`:headline:Basket: ${this.basketCount}`);
        }
        this.assetsDetailList = [...this.assetsDetailList, ...responseData];
        this.basketDataLoaded = true;
        this.filteredList = Object.assign([], this.assetsDetailList);
      });
  }
  /**
   * Clear basket
   */
  clearBasket(actionType: string, event: AnyType) {
    this.confirmDialog
      .open({
        title: actionType === 'clear' ? $localize`Clear Basket` : $localize`Remove from Basket`,
        message:
          actionType === 'clear' ? $localize`Are you sure you want to clear the basket?` : $localize`Are you sure you want to remove all items from basket?`,
      })
      .subscribe((result) => {
        if (result) {
          this.assetBasketService.clearMyBasket();
          this.removeSelectedItems('assets', this.assetsDetailList);
          this.assetsDetailList = [];
          this.filteredList = [];
          this.appliedFilters = 0;
          this.filterDetails = {};
          this.basketCount = 0;
          this.commonUtilityLib.emitNewHeaderline($localize`:headline:Basket: ${this.basketCount}`);
        } else {
          if (event.source) {
            event.source.indeterminate = true;
          }
        }
      });
  }

  /**
   * Open/Redirect to asset detail page
   * @param assetId
   */
  openAssetPage(assetId: string) {
    this.updateAssetList();
    this.router.navigate(['/assets/detail', assetId]);
  }

  protected updateAssetList() {
    this.assetCommonService.updateAssetList(this.assetsDetailList as AnyType);
    sessionStorage.setItem('assetsGalleryIdsList', JSON.stringify(this.assetsDetailList.map((asset) => asset._id)));
  }

  /**
   * Remove from basket
   * @param assetId
   * @param msg
   */
  removeFromBasket(assetId: string, event: AnyType, msg?: string) {
    this.confirmDialog
      .open({
        title: $localize`Remove from basket`,
        message: $localize`Are you sure you want to remove this asset from Basket?`,
      })
      .subscribe((confirm) => {
        if (confirm) {
          this.assetBasketService.removeFromBasket(assetId);
          const idx = this.assetsDetailList.findIndex((asset: AssetUIModel) => asset._id === assetId);
          this.removeSelectedItems('assets', [this.assetsDetailList[idx]]);
          this.assetsDetailList.splice(idx, 1);
          const index = this.filteredList.findIndex((asset: AssetUIModel) => asset._id === assetId);
          this.filteredList.splice(index, 1);
          this.clientNotificationService.successNotification(msg || $localize`Asset removed from Basket...`);
          this.basketCount = this.basketCount - 1;
          this.commonUtilityLib.emitNewHeaderline($localize`:headline:Basket: ${this.basketCount}`);
        } else {
          event.checked = true;
          event.source.checked = true;
        }
      });
  }
  /**
   * All filter conditions
   * @param filterData
   */
  applyFilters(filterData?: AnyType) {
    this.filteredList = Object.assign([], this.assetsDetailList);
    this.filterDetails = filterData ? Object.assign(this.filterDetails, filterData) : this.filterDetails;
    this.appliedFilters = 0;
    if (this.filterDetails['brand']) {
      this.appliedFilters++;
      this.filteredList = this.filteredList.filter((asset: AssetUIModel) => asset.brand.name == this.filterDetails['brand']);
    }

    if (this.filterDetails['client']) {
      this.appliedFilters++;
      this.filteredList = this.filteredList.filter((asset: AssetUIModel) => asset.client.name == this.filterDetails['client']);
    }

    if (this.filterDetails['status']) {
      this.appliedFilters++;
      this.filteredList = this.filteredList.filter((asset: AssetUIModel) => asset.status == this.filterDetails['status']);
    }

    if (this.filterDetails['assetType']) {
      this.appliedFilters++;
      this.filteredList = this.filteredList.filter((asset: AssetUIModel) => asset.assetType == this.filterDetails['assetType']);
    }

    if (this.filterDetails['endDate_from']) {
      this.appliedFilters++;
      this.filteredList = this.filteredList.filter((asset: AssetUIModel) =>
        new Date(asset.expiryDate || '') <= new Date(this.filterDetails['endDate_from']) ? 0 : 1,
      );
    }

    if (this.filterDetails['nameOrTags']) {
      this.filteredList = this.filteredList.filter(
        (asset: AssetUIModel) =>
          asset.name.toLowerCase().includes(this.filterDetails['nameOrTags'].toLowerCase()) ||
          asset.tags?.toString().toLowerCase().includes(this.filterDetails['nameOrTags'].toLowerCase()),
      );
    }
  }

  /**
   * Utility method to show/hide loader
   */
  private _showLoader(show = true) {
    this.httpCallInProgress = show;
  }

  /**
   * SearchField methods
   */

  searchTextChanged(event: AnyType) {
    this.filterDetails.nameOrTags = event;
    this.applyFilters();
  }

  clearSearch(event: AnyType) {
    this.filterDetails.nameOrTags = event;
    this.applyFilters();
  }

  toggleMobileSearch() {
    this.isMobileSearchOpen = !this.isMobileSearchOpen;
    if (this.isMobileSearchOpen) {
      document.querySelector('.actions-container')?.classList.remove('mb-8');
    }
    if (!this.isMobileSearchOpen) {
      this.searchText = '';
      document.querySelector('.actions-container')?.classList.add('mb-8');
    }
  }

  onEvent(event: AnyType) {
    this.isMobileSearchOpen = false;
    this.searchText = '';
    document.querySelector('.actions-container')?.classList.add('mb-8');
  }

  removeSelectedItems(type: string, assets: AnyType) {
    const selectedItems = JSON.parse(sessionStorage[AppSettings.SESSION_STORAGE_KEY]);
    const currentTypeIndex = selectedItems.findIndex((obj: { type: string }) => obj.type === type);
    assets.forEach((asset: AssetUIModel) => {
      const itemsFiltered = selectedItems[currentTypeIndex].items.filter((item: AnyType) => {
        return item._id !== asset._id;
      });
      selectedItems[currentTypeIndex].items = itemsFiltered;
      if (selectedItems[currentTypeIndex].selectedAll) {
        selectedItems[currentTypeIndex].selectedAll = false;
      }
    });
    sessionStorage.setItem(AppSettings.SESSION_STORAGE_KEY, JSON.stringify(selectedItems));
  }

  downloadAsset(asset: AssetUIModel) {
    this.downloadAssetsService.createDownloadData([asset]).then((downloadData) => {
      this.dialog.open<AssetDownloadComponent, AssetDownloadData>(AssetDownloadComponent, {
        width: '100%',
        maxWidth: '70rem',
        height: '100%',
        maxHeight: '43rem',
        disableClose: true,
        data: { data: downloadData },
      });
    });
  }

  addToCollection() {
    this.collectionStepperDialogService.openDialogForCollection(this.filteredList).then((result) => {
      if (result) {
        this.removeSelectedItems('assets', this.filteredList);
      }
    });
  }

  splitAssetIds(assetIds: string[]) {
    const parts = [];
    const length = assetIds.length / 4;
    for (let i = 0; i < 4; i++) {
      const part = assetIds.slice(i * length, (i + 1) * length);
      parts.push(part);
    }
    return parts;
  }
}
